// src/composables/audio/useAudioControls.ts
import { computed, ComputedRef, inject, ref } from "vue";
import { useAudio } from "@/store/modules/media/audio-list";
import type { UseAudioList } from "@/@types/audio/use_audio";
import mitt from "mitt";

export type SendOnTvPayload = {
  currentTrack: UseAudioList;
  playlist: UseAudioList[];
  isPlaying: boolean;
};

export type AudioEvents = {
  play: void;
  pause: void;
  next: void;
  prev: void;
  setCurrentTime: number; // Убрали seek, оставили только setCurrentTime
  sendOnTv: SendOnTvPayload;
};

export const audioEmitter = mitt<AudioEvents>();
export const isPlayingKey = Symbol("is-audio-playing");

/**
 * Аргументы для метода play()
 */
export type PlayOptions = {
  track: UseAudioList;
  playlist: UseAudioList[];
};

/**
 * Composable для управления аудиоплеером
 * Работает с существующим стором, но принимает данные в новом формате
 */
export function useAudioControls() {
  const audioStore = useAudio();

  // Воспроизведение
  const isPlaying = inject<ComputedRef<boolean>>(isPlayingKey);

  if (!isPlaying) {
    throw new Error(
      "Не получен статус воспроизведения. Возможно плеер был перемещён или удалён с главной страницы"
    );
  }

  const togglePlay = () => {
    audioEmitter.emit(isPlaying.value ? "pause" : "play");
  };

  // Callback для события завершения воспроизведения
  const onCompletedCallback = ref<(() => void) | null>(null);

  // ==================== COMPUTED ====================

  /** Текущий трек в старом формате (из сторы) */
  const currentTrack = computed<UseAudioList | null>(() => {
    const id = audioStore.getCurrentTrack;
    if (!id) return null;
    return audioStore.getTrackList.find((el: UseAudioList) => el._id === id) ?? null;
  });

  /** Плейлист в старом формате (из сторы) */
  const playlist = computed<UseAudioList[]>(() => {
    return audioStore.getTrackList;
  });

  // ==================== ПРОГРЕСС-БАР ====================

  /** Реактивное текущее время из стора */
  const currentTime = computed(() => audioStore.currentTime);

  /** Реактивная длительность трека из стора */
  const duration = computed(() => audioStore.duration);

  /** Вспомогательная функция форматирования времени */
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  /** Отформатированное текущее время */
  const formattedProgress = computed(() => formatTime(currentTime.value));

  /** Отформатированная длительность */
  const formattedDuration = computed(() => formatTime(duration.value));

  /** Прогресс в процентах (0-100) */
  const progressPercent = computed(() => {
    if (!duration.value) return 0;
    return (currentTime.value / duration.value) * 100;
  });

  /** Исполнители текущего трека */
  const currentSingers = computed(() => {
    return currentTrack.value?.artist ?? "";
  });

  /** Метод перемотки трека (принимает абсолютное время в секундах) */
  const setCurrentTime = (time: number) => {
    audioEmitter.emit("setCurrentTime", time);
  };

  // ==================== METHODS ====================

  const play = (options: PlayOptions): void => {
    const { track, playlist: newPlaylist } = options;

    const trackInPlaylist = newPlaylist.some((t) => t._id === track._id);
    if (!trackInPlaylist) {
      throw new Error(
        `Трек "${track.name}" не найден в переданном плейлисте. Трек должен быть частью плейлиста.`
      );
    }

    const currentTrackId = audioStore.getCurrentTrack;
    const isSameTrack = currentTrackId === track._id;

    audioStore.setTracks(newPlaylist);

    if (!isSameTrack) {
      console.log("Играем", track.name);
      audioStore.setCurrentTrack(track._id);
      audioEmitter.emit("play");
    } else {
      console.log("Плейлист обновлён, текущий трек не изменился");
    }
  };

  const onCompleted = (callback: () => void): void => {
    onCompletedCallback.value = callback;
  };

  const next = () => {
    audioEmitter.emit("next");
  };

  const previous = () => {
    audioEmitter.emit("prev");
  };

  // ==================== Отправка на ТВ ====================

  const sendOnTv = () => {
    // Проверяем, есть ли текущий трек
    if (!currentTrack.value) {
      console.warn("Невозможно отправить на ТВ: текущий трек не выбран");
      return;
    }

    // Формируем payload для отправки
    const payload: SendOnTvPayload = {
      currentTrack: currentTrack.value,
      playlist: playlist.value,
      isPlaying: isPlaying.value,
    };

    // Эмитим событие с собранными данными
    audioEmitter.emit("sendOnTv", payload);

    // Останавливаем воспроизведение на этом клиенте
    audioEmitter.emit("pause");

    console.log("Данные отправлены на ТВ", payload);
  };

  // ==================== RETURN ====================

  return {
    // Computed
    currentTrack,
    playlist,
    isPlaying,
    currentSingers,
    formattedProgress,
    formattedDuration,
    progressPercent,
    currentTime,
    duration,

    // Methods
    play,
    next,
    previous,
    onCompleted,
    togglePlay,
    setCurrentTime,
    sendOnTv,
  };
}
